import {
  AnalyticsPage,
  BigNumber,
  formatCardPercent,
  formatCount,
  Legend,
  METRIC_HELP,
  MetricCardFrame,
  RateCard,
  SectionHeading,
  SentinelReport,
  SERIES_VALUE,
  SplitBar,
  TrendChart,
} from '@/modules/sentinel-analytics';
import type { SentinelAnalyticsReport } from '@/types/sentinelAnalytics';

function failureReasonLabel(reason: string): string {
  const label = reason.replace(/^ERROR_TYPE_/, '').replace(/_/g, ' ').toLowerCase();
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function InflowOverview({ report }: { report: SentinelAnalyticsReport }) {
  const { tickets, messages, series } = report;
  const failureReasons = messages.aiGenerationFailure.reasons.map(({ reason, count }) => `${failureReasonLabel(reason)}: ${formatCount(count)}`);
  return (
    <>
      <SectionHeading>Intake</SectionHeading>
      <div className='mt-3 grid gap-x-4 gap-y-3 lg:grid-cols-2'>
        <MetricCardFrame title='Total Tickets' help={METRIC_HELP.totalTickets}>
          <BigNumber value={formatCount(tickets.totalTickets)} className='mt-auto pt-6' />
        </MetricCardFrame>
        <MetricCardFrame title='Split' help={METRIC_HELP.split}>
          <SplitBar left={{ label: 'In-scope', value: tickets.inScopeTickets }} right={{ label: 'Out of scope', value: tickets.outOfScopeTickets }} />
        </MetricCardFrame>
      </div>
      <div className='mt-3'>
        <MetricCardFrame title='Ticket Split' help={METRIC_HELP.ticketSplit} action={<Legend items={[{ label: 'In-scope', swatch: 'bg-indigo-700' }, { label: 'Out of scope', swatch: 'bg-indigo-200' }]} />}>
          <BigNumber value={formatCardPercent(tickets.inScopeShare.rate)} suffix='Avg.' />
          <TrendChart series={series} value={SERIES_VALUE.inScopeShare} background={{ value: SERIES_VALUE.outOfScopeShare, label: 'Out of scope' }} unit='percent' label='In-scope' />
        </MetricCardFrame>
      </div>
      <SectionHeading className='mt-11'>Leakage</SectionHeading>
      <div className='mt-3 grid gap-x-4 gap-y-3 lg:grid-cols-2'>
        <RateCard title='Un-assignment Rate' help={METRIC_HELP.unassignment} metric={tickets.unassignment} unit='tickets' suffix='Avg.' trend={{ series, value: SERIES_VALUE.unassignment }} />
        <RateCard title='Missed Opportunity' help={METRIC_HELP.missedOpportunity} metric={tickets.missedOpportunity} unit='tickets' suffix='Avg.' trend={{ series, value: SERIES_VALUE.missedOpportunity }} />
      </div>
      <SectionHeading className='mt-11'>Handling</SectionHeading>
      <div className='mt-3'>
        <MetricCardFrame title='Human-AI-Effort' help={METRIC_HELP.effort}>
          <SplitBar left={{ label: 'Human', value: messages.effort.human }} right={{ label: 'AI', value: messages.effort.ai }} showShare />
        </MetricCardFrame>
      </div>
      <div className='mt-3 grid gap-x-4 gap-y-3'>
        <RateCard title='Tickets Handled' help={METRIC_HELP.ticketsHandled} metric={tickets.handled} unit='tickets' suffix='Avg.' trend={{ series, value: SERIES_VALUE.handled }} />
        <RateCard title='Automation rate' help={METRIC_HELP.automation} metric={tickets.automation} suffix='Avg.' trend={{ series, value: SERIES_VALUE.automation }} />
      </div>
      <div className='mt-3 grid gap-x-4 gap-y-3 lg:grid-cols-3'>
        <RateCard title='Corrected responses' help={METRIC_HELP.correctedResponses} metric={messages.correctedResponses} suffix='Avg.' trend={{ series, value: SERIES_VALUE.correctedResponses }} />
        <RateCard title='AI Coverage' help={METRIC_HELP.aiCoverage} metric={messages.aiCoverage} unit='turns' suffix='Avg.' trend={{ series, value: SERIES_VALUE.aiCoverage }} />
        <RateCard title='AI Generation Failure' help={METRIC_HELP.aiGenerationFailure} details={failureReasons} metric={messages.aiGenerationFailure} unit='turns' suffix='Avg.' trend={{ series, value: SERIES_VALUE.aiGenerationFailure }} />
      </div>
    </>
  );
}

export default function InflowOverviewRoute() {
  return (
    <AnalyticsPage title='Overview'>
      {(query) => <SentinelReport query={query}>{(report) => <InflowOverview report={report} />}</SentinelReport>}
    </AnalyticsPage>
  );
}
